import React, { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Plus, X, Play, CheckCircle2, AlertTriangle } from "lucide-react";
import { Field, Toggle, PreviewBlock, inputCls } from "./_shared";

/**
 * Wizard Step 3 — Endpoints.
 *
 * Lists all endpoints attached to the connection with method, path,
 * timeout, retry policy, and per-row Test action. The Test action runs
 * `POST /admin/connections/{id}/endpoints/{eid}/test` and renders the
 * sanitized request + response in a TestResultPanel.
 *
 * The EndpointEditor modal supports both create and edit, including
 * retry/breaker policy, masking rules, and the test_payload that the
 * runner uses for live validation.
 */
export default function EndpointsStep({
    conn, endpoints, immutable, canEdit, canTest, onChanged,
}) {
    const [editing, setEditing] = useState(null); // null | "new" | endpoint object
    const [testRun, setTestRun] = useState(null);
    const [testing, setTesting] = useState(null); // endpoint id
    const [busyId, setBusyId] = useState(null);

    const remove = async (ep) => {
        if (!window.confirm(`Delete endpoint "${ep.name}"?`)) return;
        setBusyId(ep.id);
        try {
            await api.delete(`/admin/connections/${conn.id}/endpoints/${ep.id}`);
            toast.success("Endpoint removed");
            onChanged();
        } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail));
        } finally {
            setBusyId(null);
        }
    };

    const runTest = async (ep) => {
        setTesting(ep.id);
        setTestRun(null);
        try {
            const { data } = await api.post(`/admin/connections/${conn.id}/endpoints/${ep.id}/test`);
            setTestRun({ endpoint: ep, env: data });
            if (data.success) toast.success(`Test ok — ${data.statusCode}`);
            else toast.warning(`Test failed — ${(data.error?.errorType) || data.statusCode || "error"}`);
            onChanged();
        } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail));
        } finally {
            setTesting(null);
        }
    };

    return (
        <div className="space-y-3" data-testid="wizard-endpoints-step">
            <div className="flex items-center justify-between">
                <p className="text-xs text-white/55 max-w-2xl">
                    Each endpoint declares method, path, timeout (1–120s), retry/breaker policy, and optional masking rules.
                    Run the test panel to verify connectivity end-to-end before publishing.
                </p>
                {!immutable && canEdit && (
                    <button onClick={() => setEditing("new")}
                        data-testid="endpoint-add-btn"
                        className="px-3 py-1.5 text-xs bg-white text-black rounded-md inline-flex items-center gap-1.5">
                        <Plus className="w-3 h-3" /> Add endpoint
                    </button>
                )}
            </div>

            <div className="border border-white/10 rounded-md overflow-hidden">
                {endpoints.length === 0 ? (
                    <div className="px-3 py-10 text-center text-xs text-white/40"
                        data-testid="connection-endpoints-empty">
                        No endpoints yet. {!immutable && canEdit && "Click \"Add endpoint\" to create the first one."}
                    </div>
                ) : (
                    <table className="w-full text-xs" data-testid="connection-endpoints-table">
                        <thead className="text-[10px] uppercase tracking-wider text-white/50 bg-white/[0.03]">
                            <tr>
                                <th className="text-left px-3 py-1.5">Name</th>
                                <th className="text-left px-3 py-1.5">Method</th>
                                <th className="text-left px-3 py-1.5">Path</th>
                                <th className="text-left px-3 py-1.5">Timeout</th>
                                <th className="text-left px-3 py-1.5">Retry</th>
                                <th className="text-left px-3 py-1.5">Status</th>
                                <th />
                            </tr>
                        </thead>
                        <tbody>
                            {endpoints.map((e) => (
                                <tr key={e.id} className="border-t border-white/5"
                                    data-testid={`endpoint-row-${e.id}`}>
                                    <td className="px-3 py-1.5">{e.name}</td>
                                    <td className="px-3 py-1.5 font-mono">{e.method}</td>
                                    <td className="px-3 py-1.5 font-mono text-white/70">{e.relative_path}</td>
                                    <td className="px-3 py-1.5 text-white/65">{e.timeout_s}s</td>
                                    <td className="px-3 py-1.5 text-white/65 font-mono text-[10px]">
                                        {e.retry_policy?.enabled ? `${e.retry_policy.max_attempts}×` : "off"}
                                    </td>
                                    <td className="px-3 py-1.5">{e.status}</td>
                                    <td className="px-3 py-1.5 text-right">
                                        <div className="inline-flex gap-1">
                                            {canTest && (
                                                <button onClick={() => runTest(e)} disabled={testing === e.id}
                                                    data-testid={`endpoint-test-${e.id}`}
                                                    className="px-2 py-0.5 text-[10px] border border-emerald-300/20 text-emerald-200 rounded hover:bg-emerald-300/10 inline-flex items-center gap-1 disabled:opacity-40">
                                                    <Play className="w-2.5 h-2.5" /> {testing === e.id ? "Running…" : "Test"}
                                                </button>
                                            )}
                                            {!immutable && canEdit && (
                                                <>
                                                    <button onClick={() => setEditing(e)}
                                                        data-testid={`endpoint-edit-${e.id}`}
                                                        className="px-2 py-0.5 text-[10px] border border-white/15 rounded hover:bg-white/5">
                                                        Edit
                                                    </button>
                                                    <button onClick={() => remove(e)} disabled={busyId === e.id}
                                                        data-testid={`endpoint-delete-${e.id}`}
                                                        className="px-2 py-0.5 text-[10px] border border-rose-300/20 text-rose-200 rounded hover:bg-rose-300/10 disabled:opacity-40">
                                                        Remove
                                                    </button>
                                                </>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {testRun && <TestResultPanel run={testRun} onClose={() => setTestRun(null)} />}

            {editing && (
                <EndpointEditor
                    conn={conn}
                    endpoint={editing === "new" ? null : editing}
                    onClose={() => setEditing(null)}
                    onSaved={() => { setEditing(null); onChanged(); }}
                />
            )}
        </div>
    );
}

export function TestResultPanel({ run, onClose }) {
    const { env } = run;
    const ok = env.success;
    const errType = env.error?.errorType;
    return (
        <div className="border border-white/10 rounded-md overflow-hidden" data-testid="endpoint-test-result">
            <div className={`px-3 py-2 text-xs flex items-center justify-between ${
                ok ? "bg-emerald-300/[0.06] text-emerald-200" : "bg-rose-300/[0.06] text-rose-200"
            }`}>
                <span className="inline-flex items-center gap-2">
                    {ok ? <CheckCircle2 className="w-3.5 h-3.5" /> : <AlertTriangle className="w-3.5 h-3.5" />}
                    <span className="font-mono">{run.endpoint.method} {run.endpoint.relative_path}</span>
                    <span>·</span>
                    <span>{env.statusCode || errType || "error"}</span>
                    <span>·</span>
                    <span>{env.durationMs}ms</span>
                    {env.retryAttempts ? <span>· retried {env.retryAttempts}×</span> : null}
                </span>
                <button onClick={onClose} className="opacity-60 hover:opacity-100">
                    <X className="w-3.5 h-3.5" />
                </button>
            </div>
            {env.error?.message && (
                <div className="px-3 py-2 text-[11px] text-rose-200 border-t border-white/10 font-mono"
                    data-testid="endpoint-test-error">
                    {env.error.message}
                </div>
            )}
            <div className="grid md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-white/5">
                <PreviewBlock title="Request (sanitized)" data={env.sanitizedRequest} testId="test-result-request" />
                <PreviewBlock title="Response (sanitized)" data={env.sanitizedResponse} testId="test-result-response" />
            </div>
        </div>
    );
}

function EndpointEditor({ conn, endpoint, onClose, onSaved }) {
    const editingExisting = !!endpoint;
    const [form, setForm] = useState({
        name: endpoint?.name || "",
        description: endpoint?.description || "",
        method: endpoint?.method || "GET",
        relative_path: endpoint?.relative_path || "/",
        timeout_s: endpoint?.timeout_s ?? 30,
        max_response_bytes: endpoint?.max_response_bytes ?? 5 * 1024 * 1024,
        success_status_codes: (endpoint?.success_status_codes || [200, 201, 204]).join(","),
        retry_enabled: endpoint?.retry_policy?.enabled || false,
        retry_max_attempts: endpoint?.retry_policy?.max_attempts ?? 2,
        retry_status_codes: (endpoint?.retry_policy?.on_status_codes || [429, 500, 502, 503, 504]).join(","),
        breaker_threshold: endpoint?.retry_policy?.circuit_breaker_threshold ?? 5,
        breaker_cooldown_s: endpoint?.retry_policy?.circuit_breaker_cooldown_s ?? 300,
        idempotent: endpoint?.idempotent || false,
        test_payload: endpoint?.test_payload ? JSON.stringify(endpoint.test_payload, null, 2) : "",
        masking_header_names: (endpoint?.masking_rules || []).filter((m) => m.header_name).map((m) => m.header_name).join(","),
        masking_json_paths: (endpoint?.masking_rules || []).filter((m) => m.json_path).map((m) => m.json_path).join(","),
    });
    const [saving, setSaving] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const codes = form.success_status_codes.split(",").map((s) => parseInt(s.trim(), 10)).filter((n) => !Number.isNaN(n));
            const retryCodes = form.retry_status_codes.split(",").map((s) => parseInt(s.trim(), 10)).filter((n) => !Number.isNaN(n));
            let testPayload = null;
            if (form.test_payload.trim()) {
                try {
                    testPayload = JSON.parse(form.test_payload);
                } catch (_err) {
                    toast.error("Test payload is not valid JSON");
                    setSaving(false);
                    return;
                }
            }
            const masking = [
                ...form.masking_header_names.split(",").map((s) => s.trim()).filter(Boolean).map((header_name) => ({ header_name })),
                ...form.masking_json_paths.split(",").map((s) => s.trim()).filter(Boolean).map((json_path) => ({ json_path })),
            ];
            const body = {
                name: form.name.trim(),
                description: form.description.trim() || null,
                method: form.method,
                relative_path: form.relative_path.trim(),
                timeout_s: Number(form.timeout_s),
                max_response_bytes: Number(form.max_response_bytes),
                success_status_codes: codes.length ? codes : [200, 201, 204],
                retry_policy: {
                    enabled: form.retry_enabled,
                    max_attempts: Number(form.retry_max_attempts),
                    on_status_codes: retryCodes,
                    on_timeout: true,
                    circuit_breaker_threshold: Number(form.breaker_threshold),
                    circuit_breaker_cooldown_s: Number(form.breaker_cooldown_s),
                },
                idempotent: form.idempotent,
                test_payload: testPayload,
                masking_rules: masking,
            };
            if (editingExisting) {
                await api.patch(`/admin/connections/${conn.id}/endpoints/${endpoint.id}`, body);
                toast.success("Endpoint updated");
            } else {
                await api.post(`/admin/connections/${conn.id}/endpoints`, body);
                toast.success("Endpoint created");
            }
            onSaved();
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 overflow-y-auto"
            data-testid="endpoint-editor-modal">
            <form onSubmit={submit} className="w-full max-w-2xl bg-[#0c0c0c] border border-white/10 rounded-xl p-5 space-y-3 my-8">
                <div className="flex items-center justify-between">
                    <h3 className="text-base">{editingExisting ? "Edit endpoint" : "New endpoint"}</h3>
                    <button type="button" onClick={onClose} className="opacity-60 hover:opacity-100">
                        <X className="w-4 h-4" />
                    </button>
                </div>
                <div className="grid md:grid-cols-2 gap-3">
                    <Field label="Name" required>
                        <input type="text" value={form.name} required
                            onChange={(e) => setForm({ ...form, name: e.target.value })}
                            data-testid="endpoint-name" className={inputCls} />
                    </Field>
                    <Field label="Method">
                        <select value={form.method}
                            onChange={(e) => setForm({ ...form, method: e.target.value })}
                            data-testid="endpoint-method" className={inputCls}>
                            {["GET", "POST", "PUT", "PATCH", "DELETE"].map((m) =>
                                <option key={m} value={m}>{m}</option>)}
                        </select>
                    </Field>
                    <div className="md:col-span-2">
                        <Field label="Relative path" required>
                            <input type="text" value={form.relative_path} required
                                onChange={(e) => setForm({ ...form, relative_path: e.target.value })}
                                placeholder="/things/{id}"
                                data-testid="endpoint-path"
                                className={`${inputCls} font-mono`} />
                        </Field>
                    </div>
                    <Field label="Timeout (s)" hint="1–120">
                        <input type="number" min={1} max={120} value={form.timeout_s} required
                            onChange={(e) => setForm({ ...form, timeout_s: e.target.value })}
                            data-testid="endpoint-timeout" className={inputCls} />
                    </Field>
                    <Field label="Success codes (comma-separated)">
                        <input type="text" value={form.success_status_codes}
                            onChange={(e) => setForm({ ...form, success_status_codes: e.target.value })}
                            className={`${inputCls} font-mono`} />
                    </Field>
                </div>

                <details className="border border-white/10 rounded-md p-3">
                    <summary className="text-xs text-white/70 cursor-pointer">Retry & circuit breaker</summary>
                    <div className="grid md:grid-cols-2 gap-3 mt-3">
                        <Toggle label="Enable retry" checked={form.retry_enabled}
                            onChange={(v) => setForm({ ...form, retry_enabled: v })}
                            testId="endpoint-retry-enabled" />
                        <Field label="Max attempts">
                            <input type="number" min={1} max={8} value={form.retry_max_attempts}
                                onChange={(e) => setForm({ ...form, retry_max_attempts: e.target.value })}
                                disabled={!form.retry_enabled} className={inputCls} />
                        </Field>
                        <Field label="Retry on status codes">
                            <input type="text" value={form.retry_status_codes}
                                onChange={(e) => setForm({ ...form, retry_status_codes: e.target.value })}
                                disabled={!form.retry_enabled}
                                className={`${inputCls} font-mono`} />
                        </Field>
                        <Field label="Breaker threshold (failures)">
                            <input type="number" min={2} max={100} value={form.breaker_threshold}
                                onChange={(e) => setForm({ ...form, breaker_threshold: e.target.value })}
                                className={inputCls} />
                        </Field>
                        <Field label="Breaker cooldown (s)">
                            <input type="number" min={10} max={3600} value={form.breaker_cooldown_s}
                                onChange={(e) => setForm({ ...form, breaker_cooldown_s: e.target.value })}
                                className={inputCls} />
                        </Field>
                        <Toggle label="Idempotent (safe to retry)" checked={form.idempotent}
                            onChange={(v) => setForm({ ...form, idempotent: v })} />
                    </div>
                </details>

                <details className="border border-white/10 rounded-md p-3">
                    <summary className="text-xs text-white/70 cursor-pointer">Masking & test payload</summary>
                    <div className="grid md:grid-cols-2 gap-3 mt-3">
                        <Field label="Masked header names (comma-separated)">
                            <input type="text" value={form.masking_header_names}
                                onChange={(e) => setForm({ ...form, masking_header_names: e.target.value })}
                                placeholder="X-Internal-Token"
                                className={`${inputCls} font-mono`} />
                        </Field>
                        <Field label="Masked JSON paths (dotted)">
                            <input type="text" value={form.masking_json_paths}
                                onChange={(e) => setForm({ ...form, masking_json_paths: e.target.value })}
                                placeholder="data.user.ssn"
                                className={`${inputCls} font-mono`} />
                        </Field>
                        <div className="md:col-span-2">
                            <Field label="Test payload (JSON, used by the test runner)">
                                <textarea rows={4} value={form.test_payload}
                                    onChange={(e) => setForm({ ...form, test_payload: e.target.value })}
                                    placeholder='{"hello":"world"}'
                                    data-testid="endpoint-test-payload"
                                    className={`${inputCls} font-mono`} />
                            </Field>
                        </div>
                    </div>
                </details>

                <div className="flex justify-end gap-2 pt-2">
                    <button type="button" onClick={onClose}
                        className="px-3 py-1.5 text-xs border border-white/15 rounded-md hover:bg-white/5">
                        Cancel
                    </button>
                    <button type="submit" disabled={saving || !form.name || !form.relative_path}
                        data-testid="endpoint-save"
                        className="px-3 py-1.5 text-xs bg-white text-black rounded-md disabled:opacity-40">
                        {saving ? "Saving…" : (editingExisting ? "Save changes" : "Create endpoint")}
                    </button>
                </div>
            </form>
        </div>
    );
}
